import { Plus } from 'lucide-react'
import { useCallback, useState } from 'react'

import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import FormInput from '@/elements/FormInput'
import Text from '@/elements/Text'
import Modal from '@/components/Modal'
import Table from '@/components/Table'
import type { SubjectResponse } from '@/api/types/Subject'
import { createIndividualAlias, deleteIndividualAlias } from '@/api/organizationIndividual'
import useBreakpoints from '@/hooks/useBreakpoints'
import { useIndividualAliasTable } from '@/hooks/tables/individualAliasTable'
import toast from '@/utils/toast'
import type { IndividualAliasResponse } from '@/api/types/Organization'
import { MAX_ALIASES_PER_INDIVIDUAL } from '@/constants'

function IndividualAlias({
  individualId,
  alias,
  refetchAlias,
}: {
  individualId: SubjectResponse['id']
  alias: IndividualAliasResponse[]
  refetchAlias: () => Promise<any> // We don't care about the return type here
}) {
  const [aliasToDeleteId, setAliasToDeleteId] = useState<IndividualAliasResponse['id']>()
  const [aliasToAdd, setAliasToAdd] = useState<IndividualAliasResponse['alias']>('')

  const { isMobile } = useBreakpoints()

  const aliasTableData = useIndividualAliasTable(alias, id => setAliasToDeleteId(id))

  const handleAlias = useCallback(async (action: 'remove' | 'add') => {
    if (action === 'add' && aliasToAdd.trim() !== '') {

      const isDuplicate = alias.some(a =>
        a.alias.trim().toLowerCase() === aliasToAdd.trim().toLowerCase()
      )
      if (isDuplicate) {
        toast.error('Alias already exists')
        return
      }
      try {
        await createIndividualAlias(individualId, { alias: [aliasToAdd] })
        toast.success('Alias added successfully')
        refetchAlias()
        setAliasToAdd('')
      } catch {
        toast.error('Failed to add alias')
      }
    } else if (action === 'remove' && aliasToDeleteId) {
      try {
        await deleteIndividualAlias(individualId, aliasToDeleteId)
        toast.success('Alias removed successfully')
        refetchAlias()
        setAliasToDeleteId(undefined)
      } catch {
        toast.error('Failed to remove alias')
      }
    }
  }, [aliasToAdd, aliasToDeleteId, alias, individualId, refetchAlias])

  return (
    <Flex wfull align='flex-end' gap={6}>
      <Flex wfull inline wrap gap={3} align='flex-end'>
        <FormInput
          label='Alias/Crawler Refinement'
          name='new-alias'
          placeholder='Enter new alias'
          value={aliasToAdd}
          onChangeText={setAliasToAdd}
          disabled={alias.length >= MAX_ALIASES_PER_INDIVIDUAL}
          style={{ flex: 1 }}
        />
        <Button
          variant='primary'
          fullWidth={isMobile}
          onClick={() => handleAlias('add')}
          disabled={aliasToAdd.trim() === '' || alias.length >= MAX_ALIASES_PER_INDIVIDUAL}
        >
          <Plus size={18} />
          Add Alias
        </Button>
      </Flex>
      <Table {...aliasTableData} />
      <Modal
        isOpen={!!aliasToDeleteId}
        title='Delete Alias'
        onSave={() => handleAlias('remove')}
        onCancel={() => setAliasToDeleteId(undefined)}
        cancelLabel='No'
        saveLabel='Yes'
      >
        <Text color='black' style={{ hyphens: 'none' }}>
          Are you sure you want to delete this alias? This action cannot be undone.
        </Text>
      </Modal>
    </Flex>
  )
}
export default IndividualAlias
